import React, { useMemo } from 'react';
import { BankAccount, Debt } from '../types';
import { Scale, TrendingUp, Landmark, CreditCard as CreditCardIcon } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface NetWorthProps {
  bankAccounts: BankAccount[];
  debts: Debt[];
}

export default function NetWorth({ bankAccounts, debts }: NetWorthProps) {
  const { assets, liabilities, netWorth, assetData, liabilityData } = useMemo(() => {
    // Assets
    const liquidCash = bankAccounts
      .filter(a => a.type === 'checking' || a.type === 'savings')
      .reduce((sum, a) => sum + (a.currentBalance || 0), 0);
    
    const investments = bankAccounts
      .filter(a => a.type.startsWith('investment_'))
      .reduce((sum, a) => sum + (a.currentBalance || 0), 0);
      
    // Assuming one primary residence for now based on mortgage
    const mortgages = debts.filter(d => d.type === 'mortgage');
    const realEstate = mortgages.reduce((sum, m) => sum + (m.homeValue || 0), 0);

    const totalAssets = liquidCash + investments + realEstate;

    // Liabilities
    const mortgageDebt = mortgages.reduce((sum, m) => sum + (m.currentAmount || 0), 0);
    
    const consumerDebt = debts
      .filter(d => d.type === 'credit_card' || d.type === 'loan')
      .reduce((sum, d) => sum + (d.currentAmount || 0), 0);
      
    const locDebt = debts
      .filter(d => d.type === 'line_of_credit')
      .reduce((sum, d) => sum + (d.currentAmount || 0), 0);

    const totalLiabilities = mortgageDebt + consumerDebt + locDebt;

    const netWorth = totalAssets - totalLiabilities;

    const assetData = [
      { name: 'Liquid Cash', value: liquidCash, color: '#3b82f6' },
      { name: 'Investments', value: investments, color: '#10b981' },
      { name: 'Real Estate', value: realEstate, color: '#8b5cf6' },
    ].filter(d => d.value > 0);

    const liabilityData = [
      { name: 'Mortgages', value: mortgageDebt, color: '#ef4444' },
      { name: 'Consumer Debt', value: consumerDebt, color: '#f97316' },
      { name: 'Lines of Credit', value: locDebt, color: '#eab308' },
    ].filter(d => d.value > 0);

    return { 
      assets: totalAssets, 
      liabilities: totalLiabilities, 
      netWorth,
      assetData,
      liabilityData
    };
  }, [bankAccounts, debts]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 shadow-xl text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-2">
              <Scale className="w-4 h-4" /> Total Net Worth
            </h2>
            <div className="text-5xl font-black tracking-tight">
              ${netWorth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-slate-400 mt-2 font-medium">Your current financial footprint.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 min-w-[160px]">
              <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Total Assets
              </div>
              <div className="text-xl font-bold text-white">
                ${assets.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 min-w-[160px]">
              <div className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 rotate-180" /> Total Liabilities
              </div>
              <div className="text-xl font-bold text-white">
                ${liabilities.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets Column */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-emerald-100 overflow-hidden">
            <div className="bg-emerald-50/50 p-4 border-b border-emerald-100 flex justify-between items-center">
              <h3 className="font-bold text-emerald-900 flex items-center gap-2">
                <Landmark className="w-5 h-5 text-emerald-600" /> Assets Breakdown
              </h3>
            </div>
            
            <div className="p-6">
              <div className="h-64 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={assetData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {assetData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                {bankAccounts.map(a => (
                  <div key={a.id} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div>
                      <div className="font-semibold text-slate-800">{a.name}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">{a.type.replace('investment_', '').replace('_', ' ')}</div>
                    </div>
                    <div className="font-bold text-emerald-600">
                      ${(a.currentBalance || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
                {debts.filter(d => d.type === 'mortgage' && d.homeValue).map(m => (
                  <div key={`asset-${m.id}`} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div>
                      <div className="font-semibold text-slate-800">{m.name} Property</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Real Estate</div>
                    </div>
                    <div className="font-bold text-emerald-600">
                      ${(m.homeValue || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Liabilities Column */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-rose-100 overflow-hidden">
            <div className="bg-rose-50/50 p-4 border-b border-rose-100 flex justify-between items-center">
              <h3 className="font-bold text-rose-900 flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-rose-600" /> Liabilities Breakdown
              </h3>
            </div>
            
            <div className="p-6">
              <div className="h-64 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={liabilityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {liabilityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toLocaleString()}`, 'Owed']}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                {debts.map(d => (
                  <div key={d.id} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div>
                      <div className="font-semibold text-slate-800">{d.name}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">{d.type.replace('_', ' ')}</div>
                    </div>
                    <div className="font-bold text-rose-600">
                      ${(d.currentAmount || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
